import type { KbUpload } from "@/shared/types";

/* Caps from CONTRACT §5 and `backend/core/asgi.py`: 200 files per request, 10 MB per file,
   and the transport refuses any request body over 10 MB in total. A batch of several files
   is packed to 9.5 MB, leaving room for the multipart framing around them. */
// TODO: one file within a few hundred bytes of 10 MB passes this check and still meets a 413,
// its framing taking the body over the transport's cap.
export const MAX_FILES = 200;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_BATCH_BYTES = 9.5 * 1024 * 1024;

const mb = (bytes: number) => `${bytes / 1048576} MB`;

/** The files over the per-file cap, by name, or null when every file may be sent. */
export function checkUploads(uploads: KbUpload[]): string | null {
  const tooBig = uploads.filter((u) => u.file.size > MAX_FILE_BYTES).map((u) => u.path ?? u.file.name);
  return tooBig.length ? `Over the ${mb(MAX_FILE_BYTES)} limit per file: ${tooBig.join(", ")}.` : null;
}

/** The requests to send, in order: each at most 200 files and 9.5 MB. */
export function toBatches(uploads: KbUpload[]): KbUpload[][] {
  const batches: KbUpload[][] = [];
  let batch: KbUpload[] = [];
  let bytes = 0;
  for (const upload of uploads) {
    if (batch.length && (batch.length === MAX_FILES || bytes + upload.file.size > MAX_BATCH_BYTES)) {
      batches.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(upload);
    bytes += upload.file.size;
  }
  if (batch.length) batches.push(batch);
  return batches;
}

/** A folder pick keeps its relative paths, less the system's dot files (Finder's .DS_Store),
    as a dropped folder does; a file pick lands by name, as picked. */
export function toUploads(files: File[]): KbUpload[] {
  const uploads = files.map((file) => ({ file, path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name }));
  return uploads.filter((u) => !(u.path.includes("/") && u.file.name.startsWith(".")));
}
